import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { useBranding } from "@/lib/branding";
import { useNavigate, useSearchParams } from "react-router-dom";
import { API } from "@/lib/api";
import axios from "axios";
import { toast } from "sonner";
import { Scales, MicrosoftOutlookLogo } from "@phosphor-icons/react";
import { motion } from "framer-motion";
import InstallAppButton from "@/components/InstallAppButton";
import GuestSignInCard from "@/components/GuestSignInCard";

const DEFAULT_BG = "https://static.prod-images.emergentagent.com/jobs/1e15ab80-5652-4b57-ba7e-13d5330de2a9/images/cd5944d2391800ccd404b05edd5fabb8d4b015cb83cddd59b2ae5363d01fe3c6.png";

// Detect SSO callback synchronously, **before** the component even renders, so
// returning users never see the sign-in form flash between the Microsoft
// redirect and the dashboard. The Login component reads this once on mount.
const detectSsoCallback = () => {
  try {
    const sp = new URLSearchParams(window.location.search);
    return Boolean(sp.get("code") && sp.get("state"));
  } catch {
    return false;
  }
};

export default function Login() {
  const { user } = useAuth();
  const { branding, loaded: brandingLoaded } = useBranding();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [ssoEnabled, setSsoEnabled] = useState(false);
  const [ssoLoading, setSsoLoading] = useState(false);
  const [mode, setMode] = useState("microsoft"); // "microsoft" | "guest"
  // Initialize `exchanging` from the URL on first render so the loader screen
  // shows immediately on the SSO return-trip — no form flash.
  const [exchanging, setExchanging] = useState(() => detectSsoCallback());

  // Redirect existing sessions straight to the app.
  useEffect(() => {
    if (user) navigate("/", { replace: true });
  }, [user, navigate]);

  // Fetch SSO availability so we can show a clear "contact admin" state if it
  // isn't configured (instead of silently dropping the only sign-in path).
  useEffect(() => {
    axios
      .get(`${API}/auth/config`)
      .then((r) => setSsoEnabled(!!r.data.microsoft_sso_enabled))
      .catch((e) => console.debug("auth/config failed:", e?.response?.status || e?.message));
  }, []);

  // Exchange the SSO ?code= for a token.
  useEffect(() => {
    const code = searchParams.get("code");
    const state = searchParams.get("state");
    const errorParam = searchParams.get("error");

    if (errorParam) {
      toast.error(searchParams.get("error_description") || `Microsoft sign-in error: ${errorParam}`);
      setSearchParams({}, { replace: true });
      setExchanging(false);
      return;
    }

    if (code && state) {
      const redirect_uri = window.location.origin + "/login";
      axios
        .post(`${API}/auth/sso/callback`, null, { params: { code, state, redirect_uri } })
        .then(({ data }) => {
          localStorage.setItem("bi_token", data.token);
          localStorage.setItem("bi_user", JSON.stringify(data.user));
          toast.success(`Welcome, ${data.user.name}`);
          // Hard redirect so the AuthContext re-hydrates with the new token.
          window.location.href = "/";
        })
        .catch((err) => {
          toast.error(err?.response?.data?.detail || "Microsoft sign-in failed");
          setSearchParams({}, { replace: true });
          setExchanging(false);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startMicrosoftSSO = async () => {
    setSsoLoading(true);
    try {
      const redirect_uri = window.location.origin + "/login";
      const { data } = await axios.get(`${API}/auth/sso/start`, { params: { redirect_uri } });
      window.location.href = data.authorize_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start Microsoft sign-in");
      setSsoLoading(false);
    }
  };

  // SSO callback "Signing you in…" curtain — covers the whole viewport so the
  // user never glimpses the sign-in form between the Microsoft hand-off and
  // the dashboard. We also render it on a dark backdrop matching the left rail
  // so it blends visually with the rest of the login experience.
  if (exchanging) {
    return (
      <div data-testid="sso-exchanging" className="min-h-screen flex items-center justify-center bg-slate-900 text-white">
        <div className="text-center">
          <div className="label-tracking !text-white/60 mb-3">Microsoft</div>
          <div className="font-display text-3xl">Signing you in…</div>
          <p className="text-sm text-white/60 mt-4">
            Verifying your <span className="font-mono text-white">@{branding.allowed_domain || "barnardinc.co.za"}</span> identity.
          </p>
          <div className="mt-8 mx-auto h-1 w-32 rounded-sm bg-white/10 overflow-hidden">
            <motion.div
              className="h-full bg-white/60"
              initial={{ x: "-100%" }}
              animate={{ x: "100%" }}
              transition={{ duration: 1.2, repeat: Infinity, ease: "easeInOut" }}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Left: branding image */}
      <div className="hidden lg:flex relative overflow-hidden bg-slate-900">
        {(branding.login_image_url || (brandingLoaded && !branding.login_image_url)) && (
          <img
            src={branding.login_image_url || DEFAULT_BG}
            alt={branding.firm_name}
            className="absolute inset-0 w-full h-full object-cover"
            onError={(e) => { e.currentTarget.src = "https://images.unsplash.com/photo-1729346878607-0dfc1295ee5a?crop=entropy&cs=srgb&fm=jpg&q=85"; }}
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-br from-foreground/70 via-foreground/40 to-transparent" />
        <div className="relative z-10 flex flex-col h-full w-full p-12 text-white">
          <div className="flex items-center gap-3">
            {branding.logo_url ? (
              <img src={branding.logo_url} alt={branding.firm_name} className="h-10 w-10 rounded-sm object-cover" />
            ) : (
              <div className="h-10 w-10 rounded-sm bg-white/15 backdrop-blur-sm flex items-center justify-center">
                <Scales size={22} weight="bold" />
              </div>
            )}
            <div>
              <div className="font-display text-xl">{branding.firm_name}</div>
              <div className="label-tracking text-white/70 !text-[0.65rem]">{branding.tagline}</div>
            </div>
          </div>
          <div className="mt-auto">
            <h1 className="font-display text-5xl xl:text-6xl font-medium leading-[1] tracking-tight">
              Every lead<br/>captured.<br/>
              <span className="italic font-normal text-white/80">Every reply tracked.</span>
            </h1>
            <p className="mt-6 text-sm max-w-md text-white/80 leading-relaxed">
              A precision lead intake system for {branding.firm_name} — designed for senior counsel who refuse to let a single enquiry slip.
            </p>
          </div>
        </div>
      </div>

      {/* Right: SSO-only sign-in */}
      <div className="flex items-center justify-center p-6 sm:p-10 lg:p-16 relative">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          <div className="lg:hidden flex items-center gap-3 mb-8">
            {branding.logo_url ? (
              <img src={branding.logo_url} alt={branding.firm_name} className="h-10 w-10 rounded-sm object-cover" />
            ) : (
              <div className="h-10 w-10 rounded-sm bg-slate-900 flex items-center justify-center">
                <Scales size={22} weight="bold" className="text-white" />
              </div>
            )}
            <div className="font-display text-xl text-slate-900">{branding.firm_name}</div>
          </div>

          <div className="label-tracking">Sign in</div>
          <h2 className="font-display text-4xl sm:text-5xl font-medium tracking-tight leading-none mt-2 text-slate-900">
            {branding.tagline || "Business Grabber"}.
          </h2>

          {/* Mode switcher */}
          <div className="mt-8 inline-flex p-1 rounded-sm bg-slate-100 text-xs" data-testid="login-mode-switch">
            <button
              type="button"
              data-testid="login-mode-microsoft"
              onClick={() => setMode("microsoft")}
              className={`px-3 py-1.5 rounded-sm transition-colors ${mode === "microsoft" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-900"}`}
            >
              Staff (Microsoft)
            </button>
            <button
              type="button"
              data-testid="login-mode-guest"
              onClick={() => setMode("guest")}
              className={`px-3 py-1.5 rounded-sm transition-colors ${mode === "guest" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-900"}`}
            >
              Guest access
            </button>
          </div>

          {mode === "microsoft" ? (
            <>
              <p className="text-sm text-slate-500 mt-4 leading-relaxed">
                Restricted to <span className="font-mono text-slate-900">@{branding.allowed_domain || "barnardinc.co.za"}</span> users.
                Sign in with your Microsoft work account.
              </p>

              <button
                data-testid="microsoft-sso-btn"
                onClick={startMicrosoftSSO}
                disabled={!ssoEnabled || ssoLoading}
                className="mt-6 w-full flex items-center justify-center gap-3 px-4 py-3 rounded-sm border border-slate-300 text-sm font-medium text-slate-700 bg-white hover:bg-slate-50 hover:border-slate-400 transition-colors disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:border-slate-300"
                title={ssoEnabled ? "Sign in with Microsoft" : "Microsoft SSO is not configured — contact your administrator."}
              >
                <MicrosoftOutlookLogo size={20} weight="fill" className="text-[#0078D4]" />
                {ssoLoading ? "Redirecting to Microsoft…" : "Sign in with Microsoft"}
              </button>

              {!ssoEnabled && (
                <p
                  data-testid="sso-disabled-banner"
                  className="mt-4 rounded-sm border border-amber-200 bg-amber-50 text-amber-900 text-xs px-3 py-2"
                >
                  Microsoft sign-in is not configured. Please contact your administrator to enable it
                  in <span className="font-mono">Settings → Microsoft 365</span>.
                </p>
              )}
            </>
          ) : (
            <>
              <p className="text-sm text-slate-500 mt-4 leading-relaxed">
                For invited guest users. Sign in with your email and password. We'll email you a
                6-digit code to complete sign-in.
              </p>
              <GuestSignInCard />
            </>
          )}

          <p className="mt-10 text-xs text-slate-400 leading-relaxed">
            By signing in, you acknowledge that all lead data is confidential and subject to {branding.firm_name}'s privacy policy. © {branding.firm_name}.
          </p>

          <div className="mt-8 max-w-xs">
            <InstallAppButton />
          </div>
        </motion.div>
      </div>
    </div>
  );
}
